import type { DeathCause, GroundSample, SimulationWorld, Vec3 } from '../contracts/game'
import {
  sweepSphereAgainstCapsule,
  sweepSphereAgainstOrientedBox,
  sweepSphereAgainstSphere,
} from '../collision/SweptSphere'
import { ChunkGenerator } from './ChunkGenerator'
import type { ObstacleSlot, TrackChunkDefinition } from './ChunkTypes'
import { sampleObstaclePlacement, sampleObstacleTransform } from './ObstacleBehavior'
import { sampleChunkFrame } from './TrackFrame'

const RETAIN_BEHIND = 160
const GENERATE_AHEAD = 560

export function obstacleX(slot: ObstacleSlot, tick: number): number {
  return sampleObstacleTransform(slot, tick, { lateral: 0, vertical: 0, rotationZ: 0 }).lateral
}

export class ProceduralWorld implements SimulationWorld {
  private generator = new ChunkGenerator(1)
  private chunks: TrackChunkDefinition[] = []
  private nextIndex = 0
  private nextStartZ = 0
  private currentTick = 0

  public get activeChunks(): readonly TrackChunkDefinition[] {
    return this.chunks
  }

  public reset(seed: number): void {
    this.generator = new ChunkGenerator(seed)
    this.chunks = []
    this.nextIndex = 0
    this.nextStartZ = 0
    this.currentTick = 0
    this.ensureAhead(0)
  }

  public update(tick: number, playerZ: number): void {
    this.currentTick = tick
    let firstRetained = 0
    while (firstRetained < this.chunks.length) {
      const chunk = this.chunks[firstRetained]
      if (chunk === undefined || chunk.startZ + chunk.length >= playerZ - RETAIN_BEHIND) break
      firstRetained += 1
    }
    if (firstRetained > 0) this.chunks.splice(0, firstRetained)
    this.ensureAhead(playerZ)
  }

  public sampleTrackCenter(distance: number, out: Vec3): void {
    const chunk = this.findChunk(distance)
    if (chunk === undefined) {
      out.x = 0
      out.y = 0
      out.z = distance
      return
    }
    const frame = sampleChunkFrame(chunk, distance - chunk.startZ)
    out.x = frame.center.x
    out.y = frame.center.y
    out.z = distance
  }

  public trackCenterX(distance: number): number {
    const chunk = this.findChunk(distance)
    return chunk === undefined ? 0 : sampleChunkFrame(chunk, distance - chunk.startZ).center.x
  }

  public sampleGround(x: number, z: number, out: GroundSample): boolean {
    const chunk = this.findChunk(z)
    if (chunk === undefined) return false
    const localZ = z - chunk.startZ
    const frame = sampleChunkFrame(chunk, localZ)
    const lateral = x - frame.center.x
    if (Math.abs(lateral) > chunk.width * 0.5) return false
    if (chunk.gapStart !== null && localZ >= chunk.gapStart && localZ <= chunk.gapStart + chunk.gapLength) {
      return false
    }

    const dx = x - frame.center.x
    const dz = z - frame.center.z
    out.height = frame.center.y - (frame.up.x * dx + frame.up.z * dz) / Math.max(0.1, frame.up.y)
    out.normal.x = frame.up.x
    out.normal.y = frame.up.y
    out.normal.z = frame.up.z
    return true
  }

  public sweepPlayer(from: Vec3, to: Vec3, radius: number): DeathCause | null {
    for (const chunk of this.chunks) {
      if (to.z + radius < chunk.startZ || from.z - radius > chunk.startZ + chunk.length) continue
      for (const slot of chunk.obstacles) {
        const current = sampleObstaclePlacement(chunk, slot, this.currentTick)
        const previous = sampleObstaclePlacement(chunk, slot, Math.max(0, this.currentTick - 1))
        const relativeFrom = {
          x: from.x - previous.position.x,
          y: from.y - previous.position.y,
          z: from.z - previous.position.z,
        }
        const relativeTo = {
          x: to.x - current.position.x,
          y: to.y - current.position.y,
          z: to.z - current.position.z,
        }
        const collider = slot.collider ?? (slot.behavior === 'rotate' ? 'obb' : 'box')
        const hit = collider === 'sphere'
          ? sweepSphereAgainstSphere(relativeFrom, relativeTo, radius, {
              center: { x: 0, y: 0, z: 0 },
              radius: Math.max(slot.width, slot.height, slot.depth) * 0.5,
            })
          : collider === 'capsule'
            ? sweepSphereAgainstCapsule(relativeFrom, relativeTo, radius, {
                center: { x: 0, y: 0, z: 0 },
                radius: slot.width * 0.5,
                halfHeight: Math.max(0, slot.height * 0.5 - slot.width * 0.5),
                rotation: current.rotation,
              })
            : sweepSphereAgainstOrientedBox(relativeFrom, relativeTo, radius, {
                center: { x: 0, y: 0, z: 0 },
                halfExtents: { x: slot.width * 0.5, y: slot.height * 0.5, z: slot.depth * 0.5 },
                rotation: current.rotation,
              })
        if (hit !== null) return 'obstacle'
      }
    }
    return null
  }

  private ensureAhead(playerZ: number): void {
    while (this.nextStartZ < playerZ + GENERATE_AHEAD) {
      const chunk = this.generator.generate(this.nextIndex, this.nextStartZ)
      this.chunks.push(chunk)
      this.nextStartZ += chunk.length
      this.nextIndex += 1
    }
  }

  private findChunk(z: number): TrackChunkDefinition | undefined {
    return this.chunks.find((chunk) => z >= chunk.startZ && z < chunk.startZ + chunk.length)
  }
}
